import { WS_GRID_DEFAULT_PRESET_ID, WS_GRID_PRESET_TYPES } from '@/domain/documents/constants';
import { layoutFromPresetConfig } from '@/domain/documents/gridPreset/convert';
import type { GridLayout } from '@/domain/documents/gridPreset/schemas';
import type { SavedColumnLayout } from '@/domain/documents/savedColumnLayout';
import type { GridPresetsCache } from '@/domain/documents/queries/gridPresets';
import type { DocumentsGridPreset } from '@/domain/documents/schemas';

export type GridPresetType = (typeof WS_GRID_PRESET_TYPES)[keyof typeof WS_GRID_PRESET_TYPES];

// A presets menu entry: Default, the shared preset, or one of the list.
export type GridMenuPreset = {
  id: string;
  name: string;
  type: GridPresetType;
  // Belongs to every company.
  isAll: boolean;
  layout: GridLayout;
};

export const gridPresetsList = (cache: GridPresetsCache): DocumentsGridPreset[] => cache.result ?? [];

// The list while it loads: one reference, so a reader's memoised values do not change on every render.
export const NO_GRID_PRESETS: ReadonlyArray<DocumentsGridPreset> = Object.freeze([]);

const toMenuPreset =
  (type: GridPresetType, defaultColumns: SavedColumnLayout) =>
  (preset: DocumentsGridPreset): GridMenuPreset => ({
    id: preset.id,
    name: type === WS_GRID_PRESET_TYPES.shared ? 'Shared' : preset.name,
    type,
    isAll: !preset.company_id,
    layout: layoutFromPresetConfig(preset.config, defaultColumns),
  });

export const toDefaultMenuPreset = (defaultColumns: SavedColumnLayout): GridMenuPreset => ({
  id: WS_GRID_DEFAULT_PRESET_ID,
  name: 'Default',
  type: WS_GRID_PRESET_TYPES.default,
  isAll: false,
  layout: { filters: {}, sorting: [], columns: [...defaultColumns] },
});

// The menu, in order: Default, the shared preset (when a link brought one), then the list.
export const toMenuPresets = ({
  presets,
  shared,
  defaultColumns,
}: {
  presets: ReadonlyArray<DocumentsGridPreset>;
  shared: DocumentsGridPreset | null;
  defaultColumns: SavedColumnLayout;
}): GridMenuPreset[] => [
  toDefaultMenuPreset(defaultColumns),
  ...(shared ? [toMenuPreset(WS_GRID_PRESET_TYPES.shared, defaultColumns)(shared)] : []),
  ...presets.map(toMenuPreset(WS_GRID_PRESET_TYPES.custom, defaultColumns)),
];
